// ABOUTME: The fetch client for „Meine Verfügbarkeit“ on pro-core: the weekly template, week exceptions, semester breaks and
// ABOUTME: their hours requests. pro-core serialises with non_empty, so every answer is filled up to the app's types here.
import { handleApiError } from 'utils/api';
import { env } from '../../constants';
import {
  AVAILABILITY_API_URL,
  AVAILABILITY_EXCEPTIONS_API_URL,
  HOURS_REQUESTS_API_SEGMENT,
  SEMESTER_BREAKS_API_URL
} from './constants';
import {
  AvailabilityDay,
  AvailabilityException,
  AvailabilityExceptionDay,
  AvailabilityExceptionsResponse,
  AvailabilityResponse,
  CreatedRefResponse,
  CreateHoursRequestBody,
  SemesterBreak,
  SemesterBreakHoursRequest,
  SemesterBreaksResponse,
  UpdateAvailabilityBody,
  UpsertAvailabilityExceptionBody,
  UpsertSemesterBreakBody
} from './types';

const jsonHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json'
});

const request = async (
  token: string,
  path: string,
  init?: RequestInit
): Promise<Response> => {
  const response = await fetch(`${env.VITE_API_BASE_URL}/${path}`, {
    headers: { Authorization: `Bearer ${token}` },
    ...init
  });

  if (!response.ok) {
    if ([401, 503].includes(response.status)) {
      window.location.reload();
    }
    await handleApiError(response);
  }

  return response;
};

/**
 * pro-core writes its answers with `default-property-inclusion: non_empty`: a null and an empty list are left out, so
 * a talent without a template reads `{ "data": {} }`, an empty list `{}`, and a day without preferred time has no
 * `preferred`. The screens are owed the full shape, so the absent keys are put back here, once.
 */
type Wire<T> = { [K in keyof T]?: T[K] | null };

const dayOf = (day: Wire<AvailabilityDay>): AvailabilityDay => ({
  dayOfWeek: day.dayOfWeek!,
  available: day.available ?? [],
  preferred: day.preferred ?? []
});

const exceptionDayOf = (
  day: Wire<AvailabilityExceptionDay>
): AvailabilityExceptionDay => ({
  dayOfWeek: day.dayOfWeek!,
  unavailable: day.unavailable ?? false,
  available: day.available ?? null,
  preferred: day.preferred ?? null
});

const exceptionOf = (
  exception: Wire<AvailabilityException>
): AvailabilityException => ({
  refId: exception.refId!,
  year: exception.year!,
  calendarWeek: exception.calendarWeek!,
  wholeWeekUnavailable: exception.wholeWeekUnavailable ?? false,
  note: exception.note ?? null,
  days: exception.days ? exception.days.map(exceptionDayOf) : null
});

const hoursRequestOf = (
  request: Wire<SemesterBreakHoursRequest>
): SemesterBreakHoursRequest => ({
  refId: request.refId!,
  assignmentRefId: request.assignmentRefId!,
  requestedWeeklyMinutes: request.requestedWeeklyMinutes ?? 0,
  status: request.status!,
  decidedAt: request.decidedAt ?? null,
  decisionComment: request.decisionComment ?? null,
  raiseApplyAt: request.raiseApplyAt ?? null,
  revertApplyAt: request.revertApplyAt ?? null
});

const semesterBreakOf = (
  semesterBreak: Wire<SemesterBreak>
): SemesterBreak => ({
  refId: semesterBreak.refId!,
  startDate: semesterBreak.startDate!,
  endDate: semesterBreak.endDate!,
  note: semesterBreak.note ?? null,
  days: (semesterBreak.days ?? []).map(dayOf),
  hoursRequests: (semesterBreak.hoursRequests ?? []).map(hoursRequestOf)
});

export const fetchAvailability = async (
  token: string | undefined
): Promise<AvailabilityResponse | undefined> => {
  if (!token) return undefined;
  const response = await request(token, AVAILABILITY_API_URL);
  const json = (await response.json()) as {
    data?: { refId?: string | null; days?: Wire<AvailabilityDay>[] | null };
  };
  return {
    data: {
      refId: json.data?.refId ?? null,
      days: (json.data?.days ?? []).map(dayOf)
    }
  };
};

export const updateAvailability = async (
  token: string | undefined,
  body: UpdateAvailabilityBody
): Promise<void> => {
  if (!token) return;
  await request(token, AVAILABILITY_API_URL, {
    method: 'PUT',
    headers: jsonHeaders(token),
    body: JSON.stringify(body)
  });
};

export const fetchAvailabilityExceptions = async (
  token: string | undefined
): Promise<AvailabilityExceptionsResponse | undefined> => {
  if (!token) return undefined;
  const response = await request(token, AVAILABILITY_EXCEPTIONS_API_URL);
  const json = (await response.json()) as {
    data?: Wire<AvailabilityException>[] | null;
  };
  return { data: (json.data ?? []).map(exceptionOf) };
};

export const upsertAvailabilityException = async (
  token: string | undefined,
  year: number,
  calendarWeek: number,
  body: UpsertAvailabilityExceptionBody
): Promise<void> => {
  if (!token) return;
  await request(
    token,
    `${AVAILABILITY_EXCEPTIONS_API_URL}?calendarWeek=${calendarWeek}&year=${year}`,
    {
      method: 'PUT',
      headers: jsonHeaders(token),
      body: JSON.stringify(body)
    }
  );
};

export const deleteAvailabilityException = async (
  token: string | undefined,
  refId: string
): Promise<void> => {
  if (!token) return;
  await request(token, `${AVAILABILITY_EXCEPTIONS_API_URL}/${refId}`, {
    method: 'DELETE'
  });
};

export const fetchSemesterBreaks = async (
  token: string | undefined
): Promise<SemesterBreaksResponse | undefined> => {
  if (!token) return undefined;
  const response = await request(token, SEMESTER_BREAKS_API_URL);
  const json = (await response.json()) as {
    data?: Wire<SemesterBreak>[] | null;
  };
  return { data: (json.data ?? []).map(semesterBreakOf) };
};

export const createSemesterBreak = async (
  token: string | undefined,
  body: UpsertSemesterBreakBody
): Promise<CreatedRefResponse | undefined> => {
  if (!token) return undefined;
  const response = await request(token, SEMESTER_BREAKS_API_URL, {
    method: 'POST',
    headers: jsonHeaders(token),
    body: JSON.stringify(body)
  });
  return response.json() as Promise<CreatedRefResponse>;
};

export const updateSemesterBreak = async (
  token: string | undefined,
  refId: string,
  body: UpsertSemesterBreakBody
): Promise<void> => {
  if (!token) return;
  await request(token, `${SEMESTER_BREAKS_API_URL}/${refId}`, {
    method: 'PUT',
    headers: jsonHeaders(token),
    body: JSON.stringify(body)
  });
};

export const deleteSemesterBreak = async (
  token: string | undefined,
  refId: string
): Promise<void> => {
  if (!token) return;
  await request(token, `${SEMESTER_BREAKS_API_URL}/${refId}`, {
    method: 'DELETE'
  });
};

export const createHoursRequest = async (
  token: string | undefined,
  breakRefId: string,
  body: CreateHoursRequestBody
): Promise<CreatedRefResponse | undefined> => {
  if (!token) return undefined;
  const response = await request(
    token,
    `${SEMESTER_BREAKS_API_URL}/${breakRefId}/${HOURS_REQUESTS_API_SEGMENT}`,
    {
      method: 'POST',
      headers: jsonHeaders(token),
      body: JSON.stringify(body)
    }
  );
  return response.json() as Promise<CreatedRefResponse>;
};

export const cancelHoursRequest = async (
  token: string | undefined,
  breakRefId: string,
  requestRefId: string
): Promise<void> => {
  if (!token) return;
  await request(
    token,
    `${SEMESTER_BREAKS_API_URL}/${breakRefId}/${HOURS_REQUESTS_API_SEGMENT}/${requestRefId}`,
    { method: 'DELETE' }
  );
};
